export const POMPEI_DATABASE_SECTION_SLUGS = Object.freeze({
    pompeii: 'pompeii',
    documentazioni: 'documentazioni',
    indagini: 'indagini',
});

/**
 * Fallback list mirroring `App\Domain\Pompei\Sheets\PompeiTopographicLevelRegistry`.
 * The runtime source of truth is the backend registry, exposed via Inertia as
 * `pompei.topographic_levels`; this fallback is used for SSR/test isolation.
 */
const FALLBACK_TOPOGRAPHIC_PRESETS = Object.freeze([
    {
        id: 'regioni',
        label: 'Regioni',
        color: '#d88921',
        description: 'Suddivisione topografica della pianta della citta in macro-aree numerate.',
        menu_column: 1,
    },
    {
        id: 'insulae',
        label: 'Insulae',
        color: '#d88921',
        description: 'Isolati urbani delimitati da strade, composto da piu edifici e abitazioni.',
        menu_column: 1,
    },
    {
        id: 'strade',
        label: 'Strade',
        color: '#d88921',
        description: 'Assi viari che delimitano le insulae e connettono le aree della citta.',
        menu_column: 1,
    },
    {
        id: 'marciapiedi',
        label: 'Marciapiedi',
        color: '#d88921',
        description: 'Camminamenti urbani rialzati che separano le sedi viarie dalle insulae.',
        menu_column: 1,
    },
    {
        id: 'edifici',
        label: 'Edifici',
        color: '#d88921',
        description: 'Spazi identificabili per funzione: case, botteghe, edifici pubblici e altro; in Pompei corrispondono alle unita catastali.',
        menu_column: 1,
    },
    {
        id: 'ambienti',
        label: 'Ambienti',
        color: '#d88921',
        description: 'Singoli vani o stanze che compongono e articolano internamente un edificio.',
        menu_column: 2,
    },
    {
        id: 'pareti',
        label: 'Pareti',
        color: '#d88921',
        description: 'Paramenti murari e superfici dotate di rivestimenti o apparati decorativi.',
        menu_column: 2,
    },
    {
        id: 'pavimenti',
        label: 'Pavimenti',
        color: '#d88921',
        description: 'Superfici orizzontali e piani di calpestio, con o senza apparato decorativo.',
        menu_column: 2,
    },
    {
        id: 'arredi',
        label: 'Arredi',
        color: '#d88921',
        description: 'Elementi architettonici a carattere funzionale o decorativo.',
        menu_column: 2,
    },
    {
        id: 'strutture',
        label: 'Strutture',
        color: '#d88921',
        description: 'Elementi architettonici con funzione strutturale.',
        menu_column: 2,
    },
]);

export const resolvePompeiTopographicPresets = (runtimePresets = null) => {
    if (!Array.isArray(runtimePresets) || !runtimePresets.length) {
        return FALLBACK_TOPOGRAPHIC_PRESETS;
    }

    const fallbackById = new Map(FALLBACK_TOPOGRAPHIC_PRESETS.map(preset => [preset.id, preset]));
    const seen = new Set();

    return runtimePresets.reduce((presets, preset, index) => {
        if (!preset?.id || seen.has(preset.id)) {
            return presets;
        }

        seen.add(preset.id);
        const fallback = fallbackById.get(preset.id) ?? {};
        presets.push({
            ...fallback,
            ...preset,
            menu_column: preset.menu_column ?? fallback.menu_column ?? Math.floor(index / 5) + 1,
        });

        return presets;
    }, []);
};

export const pompeiTopographicPresets = resolvePompeiTopographicPresets();

const buildTopographicPresetMap = (topographicPresets = pompeiTopographicPresets) => {
    return new Map(resolvePompeiTopographicPresets(topographicPresets).map(preset => [preset.id, preset]));
};

export const findPompeiTopographicPreset = (id, topographicPresets = pompeiTopographicPresets) => {
    return buildTopographicPresetMap(topographicPresets).get(id) ?? null;
};

export const buildPompeiDatabaseHref = (sectionSlug, params = {}) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            query.set(key, value);
        }
    });

    const serialized = query.toString();
    return serialized ? `/database/${sectionSlug}?${serialized}` : `/database/${sectionSlug}`;
};

export const buildPompeiTopographicPresetHref = (preset) => buildPompeiDatabaseHref(
    POMPEI_DATABASE_SECTION_SLUGS.pompeii,
    {
        database_dropdown_entry: preset?.id,
    }
);

const translate = (t, key, fallback) => typeof t === 'function' ? t(key, fallback) : fallback;

const topographicCard = (preset, t = null) => ({
    id: preset.id,
    label: translate(t, `pompei.cards.${preset.id}.label`, preset.label ?? preset.id),
    description: translate(t, `pompei.cards.${preset.id}.description`, preset.description ?? ''),
    color: preset.color ?? '#d88921',
    href: buildPompeiTopographicPresetHref(preset),
});

const buildTopographicColumns = (topographicPresets, t = null) => {
    const columnsByKey = new Map();

    topographicPresets.forEach((preset, index) => {
        const parsedColumn = Number.parseInt(preset?.menu_column, 10);
        const columnKey = Number.isFinite(parsedColumn) && parsedColumn > 0
            ? parsedColumn
            : Math.floor(index / 5) + 1;

        if (!columnsByKey.has(columnKey)) {
            columnsByKey.set(columnKey, []);
        }

        columnsByKey.get(columnKey).push(topographicCard(preset, t));
    });

    return Array.from(columnsByKey.entries())
        .sort(([left], [right]) => left - right)
        .map(([, cards]) => cards);
};

const databaseCard = ({ id, label, description, color, sectionSlug, sheetName }, t = null) => ({
    id,
    label: translate(t, `pompei.cards.${id}.label`, label),
    description: translate(t, `pompei.cards.${id}.description`, description),
    color,
    href: buildPompeiDatabaseHref(sectionSlug, { currentSheet: sheetName }),
});

const pageCard = ({ id, label, description, href }, t = null) => ({
    id,
    label: translate(t, `pompei.cards.${id}.label`, label),
    description: translate(t, `pompei.cards.${id}.description`, description),
    href,
});

export const buildPompeiHomeMenuSections = (runtimeTopographicPresets = null, t = null) => {
    const topographicPresets = resolvePompeiTopographicPresets(runtimeTopographicPresets);

    return Object.freeze([
        {
            id: POMPEI_DATABASE_SECTION_SLUGS.pompeii,
            title: translate(t, 'pompei.sections.pompeii', 'Pompei'),
            description: translate(t, 'pompei.menu.sectionDescriptions.pompeii', 'Modulo di esplorazione del patrimonio archeologico di Pompei, organizzato per aree, edifici, ambienti ed elementi architettonici.'),
            columns: buildTopographicColumns(topographicPresets, t),
        },
        {
            id: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
            title: translate(t, 'pompei.sections.documentation', 'Documentazione'),
            description: translate(t, 'pompei.menu.sectionDescriptions.documentation', 'Archivio digitale per la consultazione di immagini, elaborati grafici, fonti bibliografiche e documentazione del patrimonio archeologico.'),
            columns: [[
                databaseCard({
                    id: 'bibliografia',
                    label: 'Bibliografia',
                    description: 'Riferimenti relativi a testi, contributi e studi sul sito archeologico di Pompei.',
                    color: '#2e74b7',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
                    sheetName: 'b',
                }, t),
                databaseCard({
                    id: 'immagini',
                    label: 'Immagini',
                    description: 'Documentazione grafica e fotografica del patrimonio archeologico di Pompei.',
                    color: '#2f7fbc',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
                    sheetName: 'pompei_photo',
                }, t),
                databaseCard({
                    id: 'iconografia',
                    label: 'Iconografia',
                    description: 'Elenco di soggetti figurati e mitologici degli apparati decorativi pompeiani.',
                    color: '#2c8d8f',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
                    sheetName: 'pompei_iconografia',
                }, t),
                databaseCard({
                    id: 'ra',
                    label: 'Reperti archeologici (RA)',
                    description: 'Scheda ICCD per la catalogazione dei reperti archeologici di Pompei.',
                    color: '#8a9f57',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
                    sheetName: 'iccd_ra',
                }, t),
                databaseCard({
                    id: 'epigrafia',
                    label: 'Epigrafia',
                    description: 'Raccolta delle iscrizioni pompeiane su diversi supporti e tecniche esecutive.',
                    color: '#d05042',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.documentazioni,
                    sheetName: 'epigrafia',
                }, t),
            ]],
        },
        {
            id: POMPEI_DATABASE_SECTION_SLUGS.indagini,
            title: translate(t, 'pompei.sections.investigations', 'Indagini'),
            description: translate(t, 'pompei.menu.sectionDescriptions.investigations', 'Sezione dedicata alla consultazione delle indagini archeologiche, degli scavi storici e dei dati stratigrafici relativi al sito di Pompei.'),
            columns: [[
                pageCard({
                    id: 'scavi-storici',
                    label: 'Timeline degli Scavi',
                    description: 'Visualizzazione su mappa degli scavi di Pompei lungo una timeline cronologica.',
                    href: '/scavi-storici',
                }, t),
                databaseCard({
                    id: 'scavo-archeologico',
                    label: 'Scavo Archeologico (DSC)',
                    description: 'Cantieri e indagini archeologiche effettuate nel sito di Pompei.',
                    color: '#b54a3a',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.indagini,
                    sheetName: 'dsc',
                }, t),
                databaseCard({
                    id: 'saggio-stratigrafico',
                    label: 'Saggio Stratigrafico (SAS)',
                    description: "Documentazione relativa ad un'area di scavo indagata con metodo stratigrafico.",
                    color: '#c97d5e',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.indagini,
                    sheetName: 'pompei_sas',
                }, t),
                databaseCard({
                    id: 'unita-stratigrafica',
                    label: 'Unita Stratigrafica (US)',
                    description: 'Unita minima di documentazione stratigrafica individuata durante lo scavo.',
                    color: '#dfb852',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.indagini,
                    sheetName: 'pompei_us',
                }, t),
                databaseCard({
                    id: 'materiali-archeologici',
                    label: 'Materiali Archeologici (TMA)',
                    description: 'Documentazione dei reperti mobili rinvenuti durante le operazioni di scavo.',
                    color: '#2e79a4',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.indagini,
                    sheetName: 'tma_iccd',
                }, t),
                databaseCard({
                    id: 'elaborati-grafici',
                    label: 'Elaborati Grafici (ELAG)',
                    description: 'Documentazione grafica prodotta e associata alle attivita di scavo.',
                    color: '#867d78',
                    sectionSlug: POMPEI_DATABASE_SECTION_SLUGS.indagini,
                    sheetName: 'pompei_elag',
                }, t),
            ]],
        },
    ]);
};

export const pompeiHomeMenuSections = buildPompeiHomeMenuSections();
